import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type {
  ClosingKontrolle,
  ClosingOpen,
} from '@/features/reports/api/use-closing-report-query'
import {
  formatCurrency,
  formatDatetime,
  formatWeekdayDateTime,
} from '@/features/reports/lib/format'
import { cn } from '@/lib/utils'

/**
 * The Kontrolle of the period (CHG-2026-10-accounting-close-periods, D5): one line instead of the
 * Abgleich machinery.
 *
 * With complete data it reads "Summe laut 44 Belegen € … ✓ vollständig". Why the Belege are not the
 * vor-Ort revenue — plus voucher surplus and online orders paid at the counter, minus sales on no
 * Beleg, which adds up exactly — is a disclosure the reader opens, not a tooltip — it has to work on a touch screen too, and it is no banner. When the
 * Belege and the synced data disagree the line turns red and names every close that differs, each
 * a button into that close.
 */
export function KontrolleLine({
  kontrolle,
  onSelect,
  className,
}: {
  kontrolle: ClosingKontrolle
  onSelect: (ref: string) => void
  className?: string
}) {
  const { t } = useTranslation('reports')
  const [open, setOpen] = useState(false)
  const complete = kontrolle.differences.length === 0
  const sum = t('accounting.kontrolle.sum', {
    count: kontrolle.belegCount,
    amount: formatCurrency(kontrolle.recordedCents),
  })

  if (!complete) {
    return (
      <div
        className={cn('text-sm text-destructive', className)}
        data-testid="kontrolle"
      >
        <p className="font-semibold">
          {sum} ·{' '}
          {t('accounting.kontrolle.recomputed', {
            amount: formatCurrency(kontrolle.recomputedCents),
          })}
        </p>
        <ul className="mt-1 space-y-0.5">
          {kontrolle.differences.map((d) => (
            <li key={d.ref}>
              <button
                type="button"
                className="tabular-nums hover:underline"
                onClick={() => onSelect(d.ref)}
              >
                {t('accounting.kontrolle.difference', {
                  at: formatWeekdayDateTime(d.closedAt),
                  amount: formatCurrency(d.differenceCents),
                })}
              </button>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  const hasExplanation =
    kontrolle.voucherSurplusCents !== 0 ||
    kontrolle.claimedOnlineCents !== 0 ||
    kontrolle.outsideBelegeCents !== 0
  return (
    <div className={cn('text-sm', className)} data-testid="kontrolle">
      <p>
        {sum}{' '}
        <span className="font-semibold text-emerald-700 dark:text-emerald-400">
          {t('accounting.kontrolle.complete')}
        </span>
      </p>
      {hasExplanation ? (
        <>
          <button
            type="button"
            className="mt-0.5 inline-flex items-center gap-0.5 text-xs text-muted-foreground hover:underline"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            data-print-hide
          >
            {t('accounting.kontrolle.why')}
            <ChevronDown
              className={cn(
                'h-3 w-3 transition-transform',
                open && 'rotate-180',
              )}
              aria-hidden="true"
            />
          </button>
          {open ? (
            <div className="mt-1 text-xs leading-5 text-muted-foreground">
              <p>{t('accounting.kontrolle.explanation')}</p>
              <ul data-testid="kontrolle-explanation">
                <li>
                  {t('accounting.kontrolle.surplus', {
                    amount: formatCurrency(kontrolle.voucherSurplusCents),
                  })}
                </li>
                <li>
                  {t('accounting.kontrolle.claimed', {
                    amount: formatCurrency(kontrolle.claimedOnlineCents),
                  })}
                </li>
                {/* Only when there is any: before 04.09.2026 sales carried no operator. */}
                {kontrolle.outsideBelegeCents !== 0 ? (
                  <li>
                    {t('accounting.kontrolle.outside', {
                      amount: formatCurrency(kontrolle.outsideBelegeCents),
                    })}
                  </li>
                ) : null}
              </ul>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

/**
 * What was paid after the latest close (D6): shown, never hidden, never counted — in no total, no
 * row and no export, until the next close takes it in.
 */
export function OpenLine({ open }: { open: ClosingOpen }) {
  const { t } = useTranslation('reports')
  const since = open.sinceClose ?? open.since
  return (
    <p className="text-sm text-muted-foreground" data-testid="open-amount">
      <span className="font-medium text-foreground">
        {t('accounting.open.label')}
      </span>{' '}
      {since
        ? t('accounting.open.since', {
            amount: formatCurrency(open.grossCents),
            at: formatDatetime(since),
          })
        : formatCurrency(open.grossCents)}
    </p>
  )
}
